import { supabase } from '../lib/supabase'
import { OffMarket, OffMarketInput, MarketCategory } from '../types/database'
import { AircraftTransactionsService } from './aircraft-transactions.service'

export class OffMarketService {
  /**
   * Get all off-market aircraft for a specific model
   * @deprecated Use UnifiedAircraftService.getOffMarketByCategory instead
   */
  static async getByModelId(modelId: number): Promise<OffMarket[]> {
    try {
      // Use new unified approach
      const { data: aircraft, error } = await supabase
        .from('aircraft')
        .select('*')
        .eq('is_off_market', true)
        .eq('off_market_model_id', modelId)
        .order('off_market_created_at', { ascending: false })

      if (error) {
        throw error
      }

      // Convert to old format for backward compatibility
      return (aircraft || []).map(a => ({
        id: a.aircraft_id.toString(), // Use aircraft_id as string for compatibility
        aircraft_id: a.aircraft_id,
        model_id: a.off_market_model_id || modelId,
        market_category: a.off_market_category || 'for_sale',
        created_at: a.off_market_created_at || a.created_at,
        created_by: a.off_market_created_by || null,
        updated_at: a.updated_at,
        updated_by: null
      }))
    } catch (error) {
      throw error
    }
  }

  /**
   * Get off-market aircraft by category for a specific model
   */
  static async getByModelIdAndCategory(modelId: number, category: MarketCategory): Promise<OffMarket[]> {
    try {
      const { data, error } = await supabase
        .from('off_market')
        .select('*')
        .eq('model_id', modelId)
        .eq('market_category', category)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get a specific off-market aircraft entry
   */
  static async getById(id: string): Promise<OffMarket | null> {
    try {
      const { data, error } = await supabase
        .from('off_market')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if an aircraft is already in off-market for a specific model
   * Now uses unified aircraft table approach
   */
  static async isAircraftInOffMarket(aircraftId: number, modelId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('aircraft')
        .select('aircraft_id')
        .eq('aircraft_id', aircraftId)
        .eq('is_off_market', true)
        .eq('off_market_model_id', modelId)
        .maybeSingle()

      if (error) {
        throw error
      }

      return data !== null
    } catch (error) {
      throw error
    }
  }

  /**
   * Add an aircraft to off-market
   * Now uses unified aircraft table approach
   * For "recently_sold" category, also creates a transaction record
   */
  static async create(input: OffMarketInput & {
    salePrice?: number
    saleDate?: string
    transactionNotes?: string
  }): Promise<OffMarket> {
    try {
      // Check if aircraft is already in off-market for this model
      const exists = await this.isAircraftInOffMarket(input.aircraft_id, input.model_id)
      if (exists) {
        throw new Error('Aircraft is already in off-market for this model')
      }

      // Ensure aircraft record exists in Supabase for options support
      await this.ensureAircraftRecordExists(input.aircraft_id)

      // Use new unified approach: update aircraft table directly
      const { data, error } = await supabase
        .from('aircraft')
        .update({
          is_off_market: true,
          off_market_category: input.market_category,
          off_market_model_id: input.model_id,
          off_market_created_at: new Date().toISOString()
        })
        .eq('aircraft_id', input.aircraft_id)
        .select()
        .single()

      if (error) {
        throw error
      }

      // If this is a "recently_sold" entry, create a transaction record
      if (input.market_category === 'recently_sold') {
        try {
          await AircraftTransactionsService.create({
            aircraft_id: input.aircraft_id,
            model_id: input.model_id,
            transaction_date: input.saleDate || new Date().toISOString().split('T')[0],
            sale_price: input.salePrice || null,
            transaction_notes: input.transactionNotes || null
          })
        } catch (transactionError) {
          // Don't fail the entire operation if transaction creation fails
          // The aircraft is still marked as recently sold
        }
      }

      // Return in old format for backward compatibility
      return {
        id: data.aircraft_id.toString(),
        aircraft_id: data.aircraft_id,
        model_id: data.off_market_model_id || input.model_id,
        market_category: data.off_market_category || input.market_category,
        created_at: data.off_market_created_at || data.created_at,
        created_by: data.off_market_created_by || null,
        updated_at: data.updated_at,
        updated_by: null
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Create a sale transaction for an aircraft
   * This method specifically handles moving aircraft to "recently_sold" with transaction details
   */
  static async createSaleTransaction(input: {
    aircraft_id: number
    model_id: number
    sale_price?: number
    sale_date?: string
    transaction_notes?: string
  }): Promise<{ offMarket: OffMarket; transactionId?: string }> {
    try {
      // Create the off-market entry
      const offMarketResult = await this.create({
        aircraft_id: input.aircraft_id,
        model_id: input.model_id,
        market_category: 'recently_sold',
        salePrice: input.sale_price,
        saleDate: input.sale_date,
        transactionNotes: input.transaction_notes
      })

      // Try to get the transaction ID that was created
      let transactionId: string | undefined
      try {
        const transactions = await AircraftTransactionsService.getByAircraftId(input.aircraft_id)
        // Get the most recent transaction for this aircraft
        if (transactions.length > 0) {
          transactionId = transactions[0].transaction_id
        }
      } catch (error) {
        // Ignore error fetching transaction ID
      }

      return {
        offMarket: offMarketResult,
        transactionId
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Ensure aircraft record exists in Supabase database
   */
  static async ensureAircraftRecordExists(aircraftId: number): Promise<void> {
    try {
      const { data: existingAircraft, error: checkError } = await supabase
        .from('aircraft')
        .select('aircraft_id')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') {
        return // Don't fail the main operation
      }

      if (!existingAircraft) {
        // Aircraft doesn't exist in Supabase, create it
        const { error: insertError } = await supabase
          .from('aircraft')
          .insert({
            aircraft_id: aircraftId,
            options: [] // Initialize with empty options array
          })

        if (insertError) {
          // Don't fail the main operation - off-market functionality will still work
        }
      }
    } catch (error) {
      // Don't fail the main operation
    }
  }

  /**
   * Update an off-market aircraft's category
   * Now uses unified aircraft table approach
   */
  static async updateCategory(id: string, category: MarketCategory): Promise<OffMarket> {
    try {
      // Convert string ID back to aircraft_id (since we used aircraft_id.toString() in getByModelId)
      const aircraftId = parseInt(id, 10)
      if (isNaN(aircraftId)) {
        throw new Error('Invalid aircraft ID')
      }

      // Update the off_market table directly (since getByAircraftId uses off_market table)
      const { data, error } = await supabase
        .from('off_market')
        .update({
          market_category: category,
          updated_at: new Date().toISOString()
        })
        .eq('aircraft_id', aircraftId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete an off-market aircraft entry
   * Now uses unified aircraft table approach
   */
  static async delete(id: string): Promise<void> {
    try {
      // Convert string ID back to aircraft_id
      const aircraftId = parseInt(id, 10)
      if (isNaN(aircraftId)) {
        throw new Error('Invalid aircraft ID')
      }

      // Use new unified approach: update aircraft table flags
      const { error } = await supabase
        .from('aircraft')
        .update({
          is_off_market: false,
          off_market_category: null,
          off_market_model_id: null,
          off_market_created_at: null,
          off_market_created_by: null
        })
        .eq('aircraft_id', aircraftId)
        .eq('is_off_market', true)

      if (error) {
        throw error
      }

      // Check if we should clean up the aircraft record
      await this.cleanupAircraftRecordIfNeeded(aircraftId)
    } catch (error) {
      throw error
    }
  }

  /**
   * Clean up aircraft record if it's no longer needed
   * (no off-market flags, no overrides, no options)
   * Now uses unified aircraft table approach
   */
  private static async cleanupAircraftRecordIfNeeded(aircraftId: number): Promise<void> {
    try {
      // Check if aircraft has any data that should be preserved
      const { data: aircraftRecord, error: aircraftError } = await supabase
        .from('aircraft')
        .select('options, is_off_market')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()

      if (aircraftError && aircraftError.code !== 'PGRST116') {
        return // Don't fail the main operation
      }

      if (!aircraftRecord) {
        return // Aircraft record doesn't exist
      }

      if (aircraftRecord.is_off_market) {
        // Aircraft is still off-market, don't remove
        return
      }

      // Check if aircraft has overrides
      const { data: overrides, error: overrideError } = await supabase
        .from('ac_overrides')
        .select('id')
        .eq('aircraft_id', aircraftId)

      if (overrideError) {
        return // Don't fail the main operation
      }

      if (overrides && overrides.length > 0) {
        // Aircraft has overrides, don't remove
        return
      }

      // Check if aircraft has options configured (already have aircraftRecord from above)
      if (aircraftRecord.options && aircraftRecord.options.length > 0) {
        // Aircraft has options configured, don't remove
        return
      }

      // Aircraft has no off-market flags, no overrides, and no options - safe to remove
      const { error: deleteError } = await supabase
        .from('aircraft')
        .delete()
        .eq('aircraft_id', aircraftId)

      if (deleteError) {
        // Don't throw - this is cleanup, not critical
      }
    } catch (error) {
      // Don't throw - this is cleanup, not critical
    }
  }

  /**
   * Get off-market aircraft by aircraft ID (across all models)
   */
  static async getByAircraftId(aircraftId: number): Promise<OffMarket[]> {
    try {
      const { data, error } = await supabase
        .from('off_market')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get statistics for off-market aircraft by model
   */
  static async getStatsByModelId(modelId: number): Promise<{
    total: number
    for_sale: number
    recently_sold: number
    removed_from_market: number
  }> {
    try {
      const { data, error } = await supabase
        .from('off_market')
        .select('market_category')
        .eq('model_id', modelId)

      if (error) {
        throw error
      }

      const stats = {
        total: data?.length || 0,
        for_sale: 0,
        recently_sold: 0,
        removed_from_market: 0
      }

      data?.forEach(item => {
        stats[item.market_category as MarketCategory]++
      })

      return stats
    } catch (error) {
      throw error
    }
  }
}
